import { useFormikContext } from "formik";
import React, { useState } from "react";
import { AlertCircle, Eye, EyeOff, Info } from "lucide-react";
import { CustomSelect, SelectOption } from "./CustomSelect";

interface FormFieldProps {
  name: string;
  label?: string | React.ReactNode;
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'checkbox' | 'radio' | 'date';
  placeholder?: string;
  disabled?: boolean;
  as?: 'input' | 'textarea' | 'select';
  rows?: number;
  children?: React.ReactNode;
  className?: string; // Corrected: added className
  labelClassName?: string;
  helperText?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  options?: SelectOption[];
  searchable?: boolean;
  required?: boolean;
  min?: string | number;
  max?: string | number;
  testId?: string;
  maxLength?: number;
  value?: any;
  onChange?: any;
  onBlur?: any;
  error?: string | boolean;
  selectTopSlot?: React.ReactNode;
}

// Reusable FormField Component
const FormField: React.FC<FormFieldProps> = ({
  name,
  label,
  type = 'text',
  placeholder,
  disabled = false,
  as = 'input',
  rows = 3,
  children,
  className = '',
  labelClassName = '',
  onKeyDown,
  helperText,
  options,
  searchable,
  required = false,
  min,
  max,
  testId,
  value,
  onChange,
  maxLength,
  onBlur,
  error,
  selectTopSlot,
}) => {
  const formik = useFormikContext<any>();
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const fieldProps = formik ? formik.getFieldProps(name) : { name, value, onChange, onBlur };
  const meta = formik ? formik.getFieldMeta(name) : { touched: !!error, error };
  const hasError: boolean = Boolean(meta.touched && meta.error);

  const inputType: string = type === 'password' && showPassword ? 'text' : type;

  const baseClasses = `w-full px-4 py-2 border rounded-sm focus:outline-none focus:ring-2 transition-all placeholder:text-slate-400 dark:placeholder:text-slate-500 ${hasError
    ? 'border-red-500 focus:ring-red-500'
    : 'border-gray-300 focus:ring-primary'
    } ${disabled ? 'bg-gray-100 dark:text-gray-300 dark:border-gray-600 dark:bg-gray-700 cursor-not-allowed' : 'bg-white dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600'} ${className}`;

  if (type === 'checkbox' || type === 'radio') {
    return (
      <div className={`mb-1 ${className}`}>
        <label className="flex items-center space-x-2 cursor-pointer group">
          <div className="relative flex items-center">
            <input
              {...fieldProps}
              id={name}
              type={type}
              data-testid={testId}
              disabled={disabled}
              checked={fieldProps.value}
              min={min}
              max={max}
              className={`w-5 h-5 transition-all text-primary border-gray-300 rounded-sm focus:ring-primary ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white cursor-pointer'
                }`}
            />
          </div>
          {label && (
            <span className={`text-sm font-semibold text-gray-700 dark:text-gray-200 ${labelClassName}`}>
              {label}
            </span>
          )}
        </label>
        {hasError && (
          <div className="flex items-center mt-1 text-red-600 text-sm">
            <AlertCircle size={14} className="mr-1" />
            {meta.error}
          </div>
        )}
      </div>
    );
  }

  const renderInput = () => {
    switch (as) {
      case 'textarea':
        return (
          <div>
            <textarea
              {...fieldProps}
              id={name}
              data-testid={testId}
              placeholder={placeholder}
              disabled={disabled}
              rows={rows}
              maxLength={maxLength}
              onKeyDown={onKeyDown}
              className={`${baseClasses} resize-none`}
            />
            {helperText && (
              <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1 flex items-center"><Info size={10} className="mr-1 text-gray-400 dark:text-gray-500" />{helperText}</p>
            )}
          </div>
        );
      case 'select':
        const selectOptions: SelectOption[] = options || [];

        // If no options array provided, try to extract from children (options)
        if (selectOptions.length === 0 && children) {
          React.Children.forEach(children, (child) => {
            if (React.isValidElement(child) && (child.type === 'option' || (child.type as any).displayName === 'option')) {
              const props = child.props as any;
              selectOptions.push({
                value: props.value?.toString() || '',
                label: props.children?.toString() || '',
              });
            }
          });
        }

        return (
          <CustomSelect
            options={selectOptions}
            value={fieldProps.value}
            onChange={(val) => formik ? formik.setFieldValue(name, val) : onChange?.({ target: { name, value: val } })}
            disabled={disabled}
            searchable={searchable}
            className={hasError ? 'border-red-500 rounded-sm ring-1 ring-red-500' : ''}
            placeholder={placeholder}
            topSlot={selectTopSlot}
          />
        );
      default:
        return (
          <div>
            <div className="relative">
              <input
                {...fieldProps}
                id={name}
                type={inputType}
                data-testid={testId}
                placeholder={placeholder}
                disabled={disabled}
                onKeyDown={onKeyDown}
                min={min}
                max={max}
                maxLength={maxLength}
                className={`${baseClasses} ${type === 'password' ? 'pr-10' : ''}`}
              />

              {type === 'password' && (
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <Eye size={20} /> : <EyeOff size={20} />}
                </button>
              )}
            </div>
            {helperText && (
              <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1 flex items-center"><Info size={10} className="mr-1 text-gray-400 dark:text-gray-500" />{helperText}</p>
            )}
          </div>
        );
    }
  };

  return (
    <div className={`mb-4 ${className}`}>
      {label && (
        <label
          htmlFor={name}
          className={`block text-sm font-medium text-gray-700 dark:text-white mb-1 ${labelClassName}`}
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {renderInput()}

      {hasError && (
        <div className="flex items-center mt-1 text-red-500 dark:text-red-700 text-xs">
          {/* <AlertCircle size={12} className="mr-1" /> */}
          {meta.error}
        </div>
      )}
    </div>
  );
};

export default FormField;
